import React from "react";
import clsx from 'clsx';
import Select from 'react-select';
import { ErrorMessage, FormikProps } from 'formik'
import { Attributes, Label, SubModel, Validation } from 'app/modules/tp/builders/FormBuilder';
import { Actions } from 'app/modules/tp/components/Modal/ModalEntity';

import makeAnimated from 'react-select/animated';
import { getParentKey } from "./MultiplyField";
const animatedComponents = makeAnimated();

export type Option = {
    value: string,
    label: string
    style?: any
}
export type Options = Array<Option>

export type SelectInput<T> = {
    type: "select",
    key: keyof T,
    options?: Options,
    optionsFetcher?: (entity: any) => Promise<Options>,
    dependsOn?: keyof T,
    isMulti?: boolean,
    isRequired?: boolean,
    attributes?: Attributes
    optionAttributes?: Attributes
} & Attributes & Validation & Label & SubModel

export type SelectInputFieldProps<T> = {
    formik: FormikProps<T>,
    inputObj: SelectInput<T>
    actions: Actions<T>,
    onChange?: (e: T) => void,
}

const getValue = (options: Array<Option>, values: Array<string>, isMulti: boolean) => {
    if (options !== undefined && values !== undefined) {
        return isMulti
            ? options.filter(option => values.includes(option.value))
            : options.find(option => values.includes(option.value));
    } else {
        return isMulti ? [] : ("" as any);
    }
};

const convertToArrayOfId = (values: any) => {
    let parsedValues: Array<string> = [];
    if (Array.isArray(values)) {
        parsedValues = ((values).flatMap((item: any) => {
            let itemValue: Array<string> = [];
            if (item?.id) {
                itemValue = [item?.id?.toString()];
            } else if (item) {
                itemValue = [item?.toString()];
            }
            return itemValue;
        }));
        return parsedValues;
    }
    if (typeof values === 'object' && values !== null) {
        return [(values)?.id?.toString()]
    }
    if (values) {
        return [values?.toString()];
    }
    return []
}

const SelectInputField = <T,>({ formik, inputObj, onChange }: SelectInputFieldProps<T>) => {

    const [options, setOptions] = React.useState<Array<Option>>([]);
    const [dependsOn, setDependsOn] = React.useState<any>(null);
    const [dependency, setDependencyValue] = React.useState<any>(null);

    const isRequiredField = (inputObj: SelectInput<T>) => {
        return inputObj.validation?.spec?.presence === 'required'
    }

    const values = React.useMemo(() => {
        const formikObj = formik.getFieldMeta(inputObj.key as string);
        const values = formikObj.touched ? formikObj.value : formikObj.initialValue
        return convertToArrayOfId(values);
    }, [formik.values]);

    const selectedValues = React.useMemo(() => (
        getValue(options, values, !!inputObj.isMulti)
    ), [values, options]);

    const fillOptions = React.useCallback(async () => {
        if (inputObj.options) {
            return setOptions(inputObj.options)
        }
        if (inputObj.optionsFetcher) {
            const parentKey = getParentKey(inputObj.key as string);
            const component = formik.getFieldMeta(parentKey as string).value
            const options = await inputObj.optionsFetcher(component as T[keyof T])
            return setOptions(options)
        }
    }, [dependency]);

    const onDepedencyChange = React.useCallback(async () => {
        fillOptions()
    }, [dependency]);

    React.useEffect(() => {
        onDepedencyChange()
    }, [dependency]);

    React.useEffect(() => {
        if (inputObj.dependsOn) {
            const parentKey = getParentKey(inputObj.key as string);
            const keyPath = parentKey ? `${parentKey}.${inputObj.dependsOn}` : inputObj.dependsOn;
            setDependsOn(keyPath)
        }
    }, [inputObj.dependsOn]);

    React.useEffect(() => {
        if (dependsOn != null) {
            let value = formik.getFieldMeta(dependsOn as string).value
            if (Array.isArray(value)) {
                value = value.length > 0 ? value[0] : null
            }
            setDependencyValue(value)
        }
    }, [formik.getFieldMeta(dependsOn as string).value]);

    React.useEffect(() => {
        formik.setFieldValue(inputObj.key as string, values)
        formik.setFieldTouched(inputObj.key as string, true)
    }, []);

    const changeValue = (value: any) => {
        formik.setFieldValue(inputObj.key as string, (!!inputObj.isMulti ? value.map((item: any) => item.value) : value?.value))
        onChange && onChange(formik.values)
    }

    return (<>
        {inputObj.label && (
            <label className={`fw-bold fs-6 mb-2 ${isRequiredField(inputObj) ? "required" : ""} `}>
                {inputObj.label}
            </label>
        )}
        <Select
            placeholder={"Seleziona " + (inputObj.label)?.toLowerCase()}
            closeMenuOnSelect={true}
            components={animatedComponents}
            options={options}
            isMulti={!!inputObj.isMulti}
            {...formik.getFieldProps(inputObj.key)}
            value={selectedValues}
            onChange={changeValue}
            className={clsx(
                { 'is-invalid': formik?.touched[inputObj.key] && formik.errors[inputObj.key] },
                {
                    'is-valid': formik?.touched[inputObj.key] && !formik.errors[inputObj.key],
                }
            )}
            classNamePrefix='tp-select'
            isDisabled={formik?.isSubmitting}
            {...inputObj.attributes}
        />
        {formik?.touched[inputObj.key] && formik.errors[inputObj.key] && (
            <ErrorMessage name={inputObj.key as string}
                render={(error: string) => (
                    <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                            <span role='alert'>{error}</span>
                        </div>
                    </div>
                )} />
        )}

    </>
    )
}
export default SelectInputField;
